import { Form, Typography } from 'antd';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { NExtendedMessage } from '@app/components/ExtendedMessage/ExtendedMessage.types';
import { useExtendedMessageEffects } from '@app/components/ExtendedMessage/hooks/useExtendedMessageEffects';
import {
  FRONT_WARNING,
  INSTAGRAM,
  INSTAGRAM_MAX_MESSAGE_LENGTH,
  LEADS_PATH,
  LOYALTY_PROGRAM_SENDOUTS,
  MAX_FIELD_LENGTH_5000,
  MESSAGE_TEMPLATES_PATH,
  NOTIFICATIONS_PATH,
} from '@app/constants';
import type { NSendMessageModal } from '@app/features/SendoutCreationModal/components/SendMessageModal/SendMessageModal.types';
import { useEmojiPicker } from '@app/hooks/useEmojiPicker';
import { useUserPrint } from '@app/hooks/useUserPrint';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { useLazyPreviewSendoutMessagesQuery } from '@app/services/rtkQuery/Sendouts/api';

import type { TextAreaRef } from 'antd/es/input/TextArea';

const { Text } = Typography;
const { useWatch } = Form;

export const useExtendedMessage = ({
  form,
  socialType,
  disabled,
  placeholder,
  selectedClientIds,
  formItemName,
  onChange,
}: NExtendedMessage.UseMessageFieldArgs) => {
  const { showNotification } = useContext(NotificationContext);

  const message = useWatch(formItemName, form);

  const { activeBranchId } = useUserPrint();
  const {
    isEmojiOpen,
    pickerRef,
    buttonRef: emojiVisibilityButtonRef,
    toggleEmojiPicker,
  } = useEmojiPicker();

  const [isMessagesPreviewModalOpen, setIsMessagesPreviewModalOpen] =
    useState(false);
  const [isKeyWordsOpen, setIsKeyWordsOpen] = useState(false);
  const messageUtf16Length = useMemo(() => (message ?? '').length, [message]);
  const [previewMessages, setPreviewMessages] = useState<string[] | null>(null);
  const [carriagePosition, setCarriagePosition] = useState<number | null>(null);

  const messageFieldRef = useRef<TextAreaRef>(null);

  const isLeadsService = window.location.pathname.includes(LEADS_PATH);
  const isMessageTemplatesService = window.location.pathname.includes(
    MESSAGE_TEMPLATES_PATH
  );

  const { isKeywordsLoading, sendoutKeywords } = useExtendedMessageEffects();

  const [previewSendoutMessages] = useLazyPreviewSendoutMessagesQuery();

  const isNotificationsService =
    window.location.pathname.includes(NOTIFICATIONS_PATH);

  const prevShouldShowErrorRef = useRef<boolean | null>(null);

  useEffect(() => {
    const shouldShowError = isNotificationsService && !message;

    if (prevShouldShowErrorRef.current === shouldShowError) return;
    prevShouldShowErrorRef.current = shouldShowError;

    form.setFields([
      {
        name: formItemName,
        errors: shouldShowError ? ['Поле не может быть пустым'] : [],
      },
    ]);
  }, [isNotificationsService, form, formItemName, message]);

  const messageSymbolsCount = useMemo(() => {
    const messageLengthWithEmojiCalc = [...(message || [])].length;

    return messageLengthWithEmojiCalc || 0;
  }, [message]);

  const maxLength = useMemo(() => {
    const isInstagram = socialType ? socialType === INSTAGRAM : false;

    if (isInstagram) {
      return INSTAGRAM_MAX_MESSAGE_LENGTH;
    }

    return MAX_FIELD_LENGTH_5000;
  }, [socialType]);

  const isMoreThanSymbolsMax = useMemo(() => {
    const isLastUnknownIntegration = socialType === null;
    const isMoreThanInstaMax =
      messageSymbolsCount > INSTAGRAM_MAX_MESSAGE_LENGTH;

    return isLastUnknownIntegration && isMoreThanInstaMax;
  }, [messageSymbolsCount, socialType]);

  const prepareSelectedKeywordToInsert: NSendMessageModal.PrepareSelectedKeywordToInsert =
    useCallback(
      (
        oldFieldValue,
        insertingValue,
        carriagePosition,
        isKeywordFormatting
      ) => {
        const isCarriagePosition = carriagePosition !== null;
        const textAreaMeta = messageFieldRef.current;

        if (textAreaMeta) {
          const selectionStart =
            textAreaMeta.resizableTextArea?.textArea.selectionStart ?? 0;
          const selectionEnd =
            textAreaMeta.resizableTextArea?.textArea.selectionEnd ?? 0;

          const selectedText =
            textAreaMeta.resizableTextArea?.textArea.value.substring(
              selectionStart,
              selectionEnd
            );

          if (selectedText) {
            const startFieldValuePart = oldFieldValue.slice(0, selectionStart);
            const endFieldValuePart = oldFieldValue.slice(selectionEnd);

            if (isKeywordFormatting) {
              return (
                startFieldValuePart +
                insertingValue +
                selectedText +
                insertingValue +
                endFieldValuePart
              );
            } else {
              return startFieldValuePart + insertingValue + endFieldValuePart;
            }
          }
        }

        if (!isCarriagePosition) {
          showNotification(
            'Перед вставкой значения, пожалуйста, установите курсор мыши ' +
              'на место его добавления (в поле сообщения)',
            FRONT_WARNING
          );

          return oldFieldValue;
        }

        const startFieldValuePart = oldFieldValue.slice(0, carriagePosition);
        const endFieldValuePart = oldFieldValue.slice(
          carriagePosition,
          oldFieldValue.length
        );

        const insertionResult =
          startFieldValuePart + insertingValue + endFieldValuePart;

        return insertionResult;
      },
      [showNotification]
    );

  const handleCursorPosition = useCallback(() => {
    if (!messageFieldRef?.current) {
      return;
    }

    const fieldStartPosition = 0;
    const carriagePositionFromField =
      messageFieldRef.current.resizableTextArea?.textArea.selectionStart;
    const handledCarriagePosition =
      carriagePositionFromField || fieldStartPosition;

    setCarriagePosition(handledCarriagePosition);
  }, [messageFieldRef]);

  const addExtraTextToMessage: NSendMessageModal.PointWithKeyWordProps['addExtraTextToMessage'] =
    useCallback(
      (insertingValue, isKeywordFormatting = false) => {
        const inputValue: string = message || '';

        const insertionResult = prepareSelectedKeywordToInsert(
          inputValue,
          insertingValue,
          carriagePosition,
          isKeywordFormatting
        );

        form.setFieldValue(formItemName, insertionResult);
      },
      [
        carriagePosition,
        form,
        formItemName,
        message,
        prepareSelectedKeywordToInsert,
      ]
    );

  const getDescriptionTag = (description: string, type?: string) => {
    const handledDescription =
      description.charAt(0).toLowerCase() + description.slice(1);

    switch (type) {
      case 'strong':
        return <Text strong>{handledDescription}</Text>;
      case 'italic':
        return <Text italic>{handledDescription}</Text>;
      case 'delete':
        return <Text delete>зачеркнутый текст</Text>;
      default:
        return <Text>{handledDescription}</Text>;
    }
  };

  const handleMessagePreviewClick = useCallback(async () => {
    if (!message || !message.length) {
      return showNotification(
        'Пожалуйста, введите минимум 1 символ в поле сообщения',
        FRONT_WARNING
      );
    }

    try {
      const data = await previewSendoutMessages({
        ActiveBranchId: activeBranchId,
        count: 10,
        message,
        clientIds: selectedClientIds || [],
      }).unwrap();

      setPreviewMessages(data);

      data.length && setIsMessagesPreviewModalOpen(true);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    message,
    showNotification,
    previewSendoutMessages,
    activeBranchId,
    selectedClientIds,
  ]);

  const onEmojiSelect = useCallback(
    (e: Record<string, unknown>) => {
      const hasEmoji = 'native' in e;

      if (!hasEmoji) {
        return showNotification('Эмоджи не был найден', FRONT_WARNING);
      }

      const isCorrectEmojiType = typeof e.native === 'string';

      if (!isCorrectEmojiType) {
        return showNotification('Тип эмоджи не был определен', FRONT_WARNING);
      }

      const selectedEmoji = `${e.native}`;

      const overflow =
        socialType === LOYALTY_PROGRAM_SENDOUTS
          ? messageSymbolsCount + Array.from(selectedEmoji).length - maxLength
          : messageUtf16Length + selectedEmoji.length - maxLength;

      if (overflow > 0) {
        return showNotification(
          'Сообщение уже содержит максимум символов. ' +
            `Пожалуйста, удалите минимум ${overflow} символ(ов)`,
          FRONT_WARNING
        );
      }

      form.setFieldValue(
        formItemName,
        prepareSelectedKeywordToInsert(message, selectedEmoji, carriagePosition)
      );
    },
    [
      carriagePosition,
      form,
      formItemName,
      maxLength,
      message,
      messageUtf16Length,
      messageSymbolsCount,
      socialType,
      showNotification,
      prepareSelectedKeywordToInsert,
    ]
  );

  const handleTextFormattingInfoButtonClick = useCallback(() => {
    setIsKeyWordsOpen((prev) => !prev);
  }, [setIsKeyWordsOpen]);

  const handleKeyWordsVisibility = useCallback(
    () => setIsKeyWordsOpen((prev) => !prev),
    [setIsKeyWordsOpen]
  );

  const props: NExtendedMessage.MessageFieldPassingProps = useMemo(
    () => ({
      placeholder,
      disabled,
      onChange,
      onKeyUp: handleCursorPosition,
      onMouseUp: handleCursorPosition,
    }),
    [onChange, placeholder, disabled, handleCursorPosition]
  );

  return {
    isKeyWordsOpen,
    isMessagesPreviewModalOpen,
    props,
    previewMessages,
    isEmojiOpen,
    pickerRef,
    emojiVisibilityButtonRef,
    messageFieldRef,
    isKeywordsLoading,
    sendoutKeywords,
    maxLength,
    messageSymbolsCount,
    message,
    isMoreThanSymbolsMax,
    isLeadsService,
    isMessageTemplatesService,
    toggleEmojiPicker,
    setIsKeyWordsOpen,
    addExtraTextToMessage,
    getDescriptionTag,
    handleMessagePreviewClick,
    onEmojiSelect,
    handleTextFormattingInfoButtonClick,
    setIsMessagesPreviewModalOpen,
    handleKeyWordsVisibility,
  };
};
